import { useState } from "react";
import { authClient } from "./lib/authClient";
import { AuthProvider } from "./context/AuthContext";

const C = { teal: "#00f5d4", red: "#ef476f", green: "#7ae582" };

// Read a one-time reset token from the URL (set by the emailed reset link).
function readResetToken() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("token");
}

export default function AuthGate({ children }) {
  const { data: session, isPending, refetch, signOut } = authClient.useSession();
  const [resetToken, setResetToken] = useState(readResetToken);

  // The reset link lands here with ?token=… — show the reset form regardless of
  // session state, then clean the token out of the URL when finished.
  const clearReset = () => {
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", window.location.pathname);
    }
    setResetToken(null);
  };

  if (resetToken) {
    return (
      <Shell>
        <ResetPasswordForm token={resetToken} onDone={clearReset} />
      </Shell>
    );
  }

  if (isPending) {
    return (
      <div style={S.center}>
        <div style={S.spinner} />
      </div>
    );
  }

  if (session) {
    return (
      <AuthProvider session={session} signOut={signOut}>
        {children}
      </AuthProvider>
    );
  }

  return <AuthScreen onAuthSuccess={refetch} />;
}

// Shared branded card wrapper.
function Shell({ children }) {
  return (
    <div style={S.page}>
      <div style={S.glow} />
      <div style={S.card}>
        <div style={S.brand}>
          <img src="/Logo.png" alt="Olmem" style={S.logo} />
          <div>
            <div style={S.brandTop}>MOMENTUM</div>
            <div style={S.brandBot}>TRADER</div>
          </div>
        </div>
        <div style={S.byLine}>by Olmem Technical Solutions</div>
        {children}
        <div style={S.footer}>
          <a href="/disclaimer.html" target="_blank" rel="noopener noreferrer" style={S.link}>
            Terms of Use &amp; Risk Disclaimer
          </a>
        </div>
      </div>
    </div>
  );
}

function AuthScreen({ onAuthSuccess }) {
  const [view, setView] = useState("signin"); // signin | signup | forgot
  const [notice, setNotice] = useState("");

  return (
    <Shell>
      {view !== "forgot" && (
        <div style={S.tabs}>
          <button
            onClick={() => { setView("signin"); setNotice(""); }}
            style={{ ...S.tab, ...(view === "signin" ? S.tabActive : {}) }}
          >Sign In</button>
          <button
            onClick={() => { setView("signup"); setNotice(""); }}
            style={{ ...S.tab, ...(view === "signup" ? S.tabActive : {}) }}
          >Create Account</button>
        </div>
      )}

      {notice && <div style={S.success}>{notice}</div>}

      {view === "signin" && (
        <SignInForm onSuccess={onAuthSuccess} onForgot={() => { setView("forgot"); setNotice(""); }} />
      )}
      {view === "signup" && (
        <SignUpForm onSuccess={() => { setView("signin"); setNotice("Account created — please sign in."); }} />
      )}
      {view === "forgot" && (
        <ForgotPasswordForm
          onBack={() => { setView("signin"); setNotice(""); }}
          onSent={() => { setView("signin"); setNotice("If that email exists, a reset link is on its way."); }}
        />
      )}
    </Shell>
  );
}

function SignInForm({ onSuccess, onForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error: err } = await authClient.signIn.email({ email, password });
    if (err) {
      setError(err.message || "Sign in failed");
      setLoading(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} style={S.form}>
      <div style={S.fieldLabel}>Email</div>
      <input type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="you@example.com" autoComplete="email" required style={S.input} />
      <div style={S.fieldLabel}>Password</div>
      <input type="password" value={password} onChange={e => setPassword(e.target.value)}
        placeholder="••••••••" autoComplete="current-password" required style={S.input} />
      {error && <div style={S.error}>{error}</div>}
      <button type="submit" disabled={loading} style={S.btn}>
        {loading ? "SIGNING IN…" : "SIGN IN"}
      </button>
      <button type="button" onClick={onForgot} style={S.linkBtn}>
        Forgot password?
      </button>
    </form>
  );
}

function SignUpForm({ onSuccess }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error: err } = await authClient.signUp.email({ name, email, password });
    if (err) {
      setError(err.message || "Registration failed");
      setLoading(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} style={S.form}>
      <div style={S.fieldLabel}>Name</div>
      <input type="text" value={name} onChange={e => setName(e.target.value)}
        placeholder="Your name" autoComplete="name" required style={S.input} />
      <div style={S.fieldLabel}>Email</div>
      <input type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="you@example.com" autoComplete="email" required style={S.input} />
      <div style={S.fieldLabel}>
        Password <span style={{ color: "rgba(255,255,255,0.25)", fontWeight: 400 }}>(min 8 chars)</span>
      </div>
      <input type="password" value={password} onChange={e => setPassword(e.target.value)}
        placeholder="••••••••" autoComplete="new-password" minLength={8} required style={S.input} />
      {error && <div style={S.error}>{error}</div>}
      <button type="submit" disabled={loading} style={S.btn}>
        {loading ? "CREATING ACCOUNT…" : "CREATE ACCOUNT"}
      </button>
    </form>
  );
}

// Step 1 of reset: request a link to your email.
function ForgotPasswordForm({ onBack, onSent }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error: err } = await authClient.forgetPassword({
      email,
      redirectTo: "/reset-password",
    });
    if (err) {
      setError(err.message || "Could not send reset email");
      setLoading(false);
    } else {
      onSent();
    }
  };

  return (
    <form onSubmit={handleSubmit} style={S.form}>
      <div style={S.heading}>Reset password</div>
      <div style={S.hint}>Enter your account email and we'll send you a reset link.</div>
      <div style={S.fieldLabel}>Email</div>
      <input type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="you@example.com" autoComplete="email" required style={S.input} />
      {error && <div style={S.error}>{error}</div>}
      <button type="submit" disabled={loading} style={S.btn}>
        {loading ? "SENDING…" : "SEND RESET LINK"}
      </button>
      <button type="button" onClick={onBack} style={S.linkBtn}>
        Back to sign in
      </button>
    </form>
  );
}

// Step 2 of reset: the page the emailed link lands on (carries ?token=…).
function ResetPasswordForm({ token, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match"); return; }
    if (password.length < 8)  { setError("Password must be at least 8 characters"); return; }
    setLoading(true);
    const { error: err } = await authClient.resetPassword({ newPassword: password, token });
    if (err) {
      setError(err.message || "Reset failed — the link may have expired. Request a new one.");
      setLoading(false);
    } else {
      setDone(true);
    }
  };

  if (done) {
    return (
      <div style={S.form}>
        <div style={S.heading}>Password updated</div>
        <div style={S.success}>Your password has been changed. You can now sign in.</div>
        <button type="button" onClick={onDone} style={S.btn}>GO TO SIGN IN</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={S.form}>
      <div style={S.heading}>Choose a new password</div>
      <div style={S.fieldLabel}>
        New password <span style={{ color: "rgba(255,255,255,0.25)", fontWeight: 400 }}>(min 8 chars)</span>
      </div>
      <input type="password" value={password} onChange={e => setPassword(e.target.value)}
        placeholder="••••••••" autoComplete="new-password" minLength={8} required style={S.input} />
      <div style={S.fieldLabel}>Confirm password</div>
      <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
        placeholder="••••••••" autoComplete="new-password" minLength={8} required style={S.input} />
      {error && <div style={S.error}>{error}</div>}
      <button type="submit" disabled={loading} style={S.btn}>
        {loading ? "UPDATING…" : "UPDATE PASSWORD"}
      </button>
      <button type="button" onClick={onDone} style={S.linkBtn}>
        Cancel
      </button>
    </form>
  );
}

const S = {
  page: { fontFamily: "'JetBrains Mono','SF Mono','Fira Code',monospace", background: "#0a0e17", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, position: "relative", overflow: "hidden" },
  glow: { position: "fixed", top: -200, right: -200, width: 500, height: 500, background: "radial-gradient(circle, rgba(0,245,212,0.07) 0%, transparent 70%)", pointerEvents: "none" },
  center: { background: "#0a0e17", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" },
  spinner: { width: 32, height: 32, border: "3px solid rgba(0,245,212,0.2)", borderTop: `3px solid ${C.teal}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  card: { width: "100%", maxWidth: 420, position: "relative", zIndex: 1 },
  brand: { display: "flex", alignItems: "center", gap: 12, marginBottom: 6 },
  logo: { height: 48, width: "auto" },
  brandTop: { fontSize: 11, color: C.teal, letterSpacing: 3, fontWeight: 700 },
  brandBot: { fontSize: 22, fontWeight: 800, color: "#fff", marginTop: 2 },
  byLine: { fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginBottom: 24 },
  tabs: { display: "flex", gap: 4, marginBottom: 20, background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: 4, border: "1px solid rgba(255,255,255,0.06)" },
  tab: { flex: 1, padding: "8px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, letterSpacing: 1, fontFamily: "inherit", background: "transparent", color: "rgba(255,255,255,0.35)", transition: "all 0.2s" },
  tabActive: { background: "rgba(0,245,212,0.15)", color: C.teal, border: "1px solid rgba(0,245,212,0.25)" },
  form: { display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 },
  heading: { fontSize: 14, fontWeight: 800, color: "#fff", letterSpacing: 1, marginBottom: 4 },
  hint: { fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 8, lineHeight: 1.5 },
  fieldLabel: { fontSize: 10, color: "rgba(255,255,255,0.4)", letterSpacing: 1, fontWeight: 700, marginTop: 4 },
  input: { width: "100%", padding: "11px 12px", borderRadius: 8, fontSize: 13, background: "rgba(0,0,0,0.4)", color: "#e0e6ed", border: "1px solid rgba(255,255,255,0.12)", outline: "none", fontFamily: "inherit", boxSizing: "border-box" },
  error: { fontSize: 11, color: C.red, background: "rgba(239,71,111,0.1)", border: "1px solid rgba(239,71,111,0.25)", borderRadius: 6, padding: "8px 12px" },
  success: { fontSize: 11, color: C.green, background: "rgba(122,229,130,0.1)", border: "1px solid rgba(122,229,130,0.25)", borderRadius: 6, padding: "8px 12px", marginBottom: 12 },
  btn: { marginTop: 4, padding: "12px", borderRadius: 8, border: "1px solid rgba(0,245,212,0.35)", background: "linear-gradient(135deg, rgba(0,245,212,0.18), rgba(122,229,130,0.12))", color: C.teal, fontSize: 12, fontWeight: 700, letterSpacing: 2, cursor: "pointer", fontFamily: "inherit" },
  linkBtn: { marginTop: 4, padding: "6px", border: "none", background: "transparent", color: "rgba(255,255,255,0.4)", fontSize: 11, fontWeight: 600, letterSpacing: 1, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" },
  footer: { textAlign: "center", paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)" },
  link: { fontSize: 10, color: "rgba(255,255,255,0.25)", textDecoration: "underline" },
};
